import {
  IE3ConsignmentHouseLevel,
  IE3ConsignmentProcess,
  IE3GoodsItem,
  IE3Party,
  IE3TransportEquipment
} from "@portbase/bezoekschip-service-typescriptmodels";
import {ErrorPointer, ErrorWithPointers} from "../../common/errors/errors";
import {ConsignmentUtils} from "./consignment.utils";

/**
 * Maps TSD (TemporaryStorage Declaration) error tags from the DEC/DED response
 * to UI ErrorPointers for field highlighting in the consignment details screen.
 *
 * Error tags come in the format: "/B2SW[1]/Declaration[2]/Consignment[1]/HouseConsignment[1]/Consignee/Name"
 * These are parsed and mapped to the ConsignmentProcess model for navigation and highlighting.
 */
export class ConsignmentTsUtils {

  /**
   * Parse a TSD error tag into ErrorPointers that can be used for UI field highlighting.
   *
   * Resolution strategy:
   * 1. If the error has a tag in the format "/B2SW[n]/Declaration[n]/...", extract the last segment's
   *    tag code and look it up in the tag_index for the most reliable resolution.
   * 2. Otherwise, normalize the tag path (strip /B2SW[n]/ prefix and indices) and look it up in paths.
   * 3. Walk the resolved path segments to map them to the ConsignmentProcess model.
   */
  static parseErrorPointers(consignment: IE3ConsignmentProcess, tag: string): ErrorPointer[] {
    if (!tag || !consignment) {
      return [];
    }

    const resolved = this.resolveTag(tag);
    if (!resolved) {
      return [];
    }

    return this.mapPathToPointers(consignment, resolved.xmlPath, resolved.indices);
  }

  /**
   * Formats a human-readable summary string from an error description and its resolved pointers.
   * Similar to how ENS adds " at <pointer>" to the summary.
   *
   * Example output: "For declaration MRN must NOT be populated (5218) at House B/L RAMDED26062603/1 > MRN"
   */
  static formatSummary(description: string, code: string, pointers: ErrorPointer[]): string {
    const base = `${description}${code ? ' (' + code + ')' : ''}`;
    const pointerDescriptions = pointers
      ?.filter(p => p.description)
      ?.map(p => p.description);

    if (pointerDescriptions?.length) {
      return `${base} at ${pointerDescriptions.join(' > ')}`;
    }
    return base;
  }

  /**
   * Resolves a tag to a human-readable path for the overview screen.
   * Returns the full breadcrumb path (e.g., "Good #1 > Package type" or "Consignee > EORI number").
   */
  static resolveTagPath(tag: string): string | null {
    if (!tag) return null;

    const resolved = this.resolveTag(tag);
    if (!resolved) return null;

    const parts: string[] = [];
    const xmlPath = resolved.xmlPath;
    const indices = resolved.indices;

    // Add HouseConsignment context
    if (xmlPath.includes('HouseConsignment')) {
      const houseIdx = indices['HouseConsignment'];
      parts.push(houseIdx ? `House B/L #${houseIdx}` : 'House B/L');
    }

    // Add ConsignmentItem (goods) context
    if (xmlPath.includes('ConsignmentItem')) {
      const itemIdx = indices['ConsignmentItem'];
      parts.push(itemIdx ? `Good #${itemIdx}` : 'Good');
    }

    // Add TransportEquipment context (only if not nested inside ConsignmentItem)
    if (xmlPath.includes('TransportEquipment') && !xmlPath.includes('ConsignmentItem/TransportEquipment')) {
      const eqIdx = indices['TransportEquipment'];
      parts.push(eqIdx ? `Equipment #${eqIdx}` : 'Equipment');
    }

    // Add party context if applicable
    if (this.isPartyPath(xmlPath)) {
      parts.push(this.getPartyLabel(xmlPath));
    }

    // Add the field label
    const screenPointer = this.getScreenPointer(xmlPath, null, indices);
    if (screenPointer) {
      parts.push(screenPointer);
    }

    return parts.length ? parts.join(' > ') : null;
  }

  /**
   * Formats a summary string for the overview screen (without model references).
   * Shows the full path (e.g., "Adress must be filled (5270) at Consignee > Number").
   */
  static formatOverviewSummary(description: string, code: string, tag: string): string {
    const base = `${description}${code ? ' (' + code + ')' : ''}`;
    const path = this.resolveTagPath(tag);
    return path ? `${base} at ${path}` : base;
  }

  /**
   * Merges related address errors (StreetNumberID + PostOfficeBoxID) for the same party
   * into a single error with both pointers, replacing the description with a clearer message.
   *
   * When customs returns errors on both StreetNumberID and PostOfficeBoxID for the same party,
   * it means "fill either house number or P.O. Box". We merge these into one error to avoid confusion.
   */
  static mergeAddressErrors(errors: ErrorWithPointers[]): ErrorWithPointers[] {
    if (!errors?.length) return errors;

    const merged: ErrorWithPointers[] = [];
    const consumed = new Set<number>();

    for (let i = 0; i < errors.length; i++) {
      if (consumed.has(i)) continue;

      const error = errors[i];
      const isStreetNumber = error.pointers?.some(p => p.screenPointer === 'Number');
      const isPostOfficeBox = error.pointers?.some(p => p.screenPointer === 'Post office box');

      if (isStreetNumber || isPostOfficeBox) {
        // Find the matching counterpart for the same party
        const partyModel = error.pointers?.find(p => p.type === 'consignor' || p.type === 'consignee' || p.type === 'notifyParty')?.model;
        const matchIndex = errors.findIndex((other, j) => {
          if (j <= i || consumed.has(j)) return false;
          const otherPartyModel = other.pointers?.find(p => p.type === 'consignor' || p.type === 'consignee' || p.type === 'notifyParty')?.model;
          if (partyModel !== otherPartyModel) return false;
          const otherIsStreetNumber = other.pointers?.some(p => p.screenPointer === 'Number');
          const otherIsPostOfficeBox = other.pointers?.some(p => p.screenPointer === 'Post office box');
          return (isStreetNumber && otherIsPostOfficeBox) || (isPostOfficeBox && otherIsStreetNumber);
        });

        if (matchIndex >= 0) {
          consumed.add(matchIndex);
          const other = errors[matchIndex];
          const combinedPointers = [...error.pointers, ...other.pointers.filter(p => p.screenPointer === 'Number' || p.screenPointer === 'Post office box')];
          const partyLabel = error.pointers?.find(p => p.type === 'consignor' || p.type === 'consignee' || p.type === 'notifyParty')?.description || '';
          const humanDescription = 'Fill either house number or P.O. Box';
          merged.push({
            header: humanDescription,
            summary: `${humanDescription} at ${error.pointers?.filter(p => p.description && p.screenPointer !== 'Number' && p.screenPointer !== 'Post office box').map(p => p.description).join(' > ')}`,
            pointers: combinedPointers
          });
        } else {
          merged.push(error);
        }
      } else {
        merged.push(error);
      }
    }

    return merged;
  }

  /**
   * Resolves an error tag to a normalized XML path and extracted indices.
   *
   * Input example: "/B2SW[1]/Declaration[2]/Consignment[1]/HouseConsignment[1]/Consignee/Name"
   * Output: { xmlPath: "Declaration/Consignment/HouseConsignment/Consignee/Name", indices: { HouseConsignment: 1, ... } }
   */
  private static resolveTag(tag: string): { xmlPath: string; indices: Record<string, number> } | null {
    // Strip the /B2SW[n]/ prefix
    const withoutPrefix = tag.replace(/^\/B2SW\[\d+\]\//, '');
    if (!withoutPrefix) {
      return null;
    }

    // Parse segments with their indices
    const segments: { name: string; index: number | null }[] = [];

    // Split by '/' and parse each segment
    const parts = withoutPrefix.split('/');
    for (const part of parts) {
      const segMatch = part.match(/^([A-Za-z_][A-Za-z0-9_]*)(?:\[(\d+)\])?$/);
      if (segMatch) {
        segments.push({
          name: segMatch[1],
          index: segMatch[2] ? parseInt(segMatch[2], 10) : null
        });
      }
    }

    if (segments.length === 0) {
      return null;
    }

    // Build the normalized path (without indices) and collect indices for repeatable segments
    const normalizedPath = segments.map(s => s.name).join('/');
    const indices: Record<string, number> = {};
    segments.forEach(s => {
      if (s.index != null) {
        indices[s.name] = s.index;
      }
    });

    return {xmlPath: normalizedPath, indices};
  }

  /**
   * Maps a resolved XML path to ErrorPointer objects that reference the correct
   * model objects (houseConsignment, goodsItem, equipment) for UI navigation.
   */
  private static mapPathToPointers(
    consignment: IE3ConsignmentProcess,
    xmlPath: string,
    indices: Record<string, number>
  ): ErrorPointer[] {
    const label = this.getScreenPointer(xmlPath, null, indices) || xmlPath.split('/').pop() || xmlPath;
    const pointers: ErrorPointer[] = [];

    // Determine which level the error points to based on the path hierarchy
    const isHouseLevel = xmlPath.includes('HouseConsignment');
    const isConsignmentItemInHouse = xmlPath.includes('HouseConsignment/ConsignmentItem');
    const isConsignmentItemDirect = !isHouseLevel && xmlPath.includes('ConsignmentItem');
    const isTransportEquipmentInHouse = xmlPath.includes('HouseConsignment/ConsignmentItem/TransportEquipment')
      || xmlPath.includes('HouseConsignment/TransportEquipment');
    const isTransportEquipmentDirect = !isHouseLevel && xmlPath.includes('TransportEquipment');

    // --- Master level pointer ---
    if (!isHouseLevel) {
      pointers.push({
        description: `Master B/L ${consignment.consignmentMasterLevel.consignmentNumber}`,
        identification: consignment.consignmentMasterLevel.consignmentNumber,
        model: consignment.consignmentMasterLevel,
        type: "masterLevel"
      });

      // Direct ConsignmentItem (goods item at master level)
      if (isConsignmentItemDirect) {
        const goodsItem = this.resolveGoodsItem(
          consignment.consignmentMasterLevel.goodsItems,
          indices['ConsignmentItem']
        );
        if (goodsItem) {
          pointers.push({
            description: `Good ${ConsignmentUtils.goodsItemCode(goodsItem.commodity)}`,
            index: goodsItem.goodsItemNumber - 1,
            identification: `${goodsItem.goodsItemNumber}`,
            model: goodsItem,
            type: "goodsItem"
          });
        }
      }

      // Direct TransportEquipment at consignment level
      if (isTransportEquipmentDirect && !isConsignmentItemDirect) {
        const equipment = this.resolveTransportEquipment(consignment, indices['TransportEquipment']);
        if (equipment) {
          pointers.push({
            description: `Equipment ${equipment.containerIdentificationNumber}`,
            identification: equipment.containerIdentificationNumber,
            model: equipment,
            type: "equipment"
          });
        }
      }

      // Add the field-level pointer with screenPointer for highlighting
      this.addFieldPointer(pointers, xmlPath, label, consignment, null, indices);
      return pointers;
    }

    // --- House level pointer ---
    const houseIndex = indices['HouseConsignment'] != null ? indices['HouseConsignment'] - 1 : 0;
    const houseConsignment = consignment.consignmentMasterLevel.consignmentsHouseLevel?.[houseIndex];

    if (houseConsignment) {
      pointers.push({
        description: `House B/L ${houseConsignment.consignmentNumber}`,
        identification: houseConsignment.consignmentNumber,
        model: houseConsignment,
        type: "houseLevel"
      });

      // ConsignmentItem within HouseConsignment (goods item at house level)
      if (isConsignmentItemInHouse) {
        const goodsItem = this.resolveGoodsItem(
          houseConsignment.goodsItems,
          indices['ConsignmentItem']
        );
        if (goodsItem) {
          pointers.push({
            description: `Good ${ConsignmentUtils.goodsItemCode(goodsItem.commodity)}`,
            index: goodsItem.goodsItemNumber - 1,
            identification: `${goodsItem.goodsItemNumber}`,
            model: goodsItem,
            type: "goodsItem"
          });
        }

        // TransportEquipment nested inside HouseConsignment/ConsignmentItem
        if (isTransportEquipmentInHouse) {
          const equipment = this.resolveTransportEquipment(consignment, indices['TransportEquipment']);
          if (equipment) {
            pointers.push({
              description: `Equipment ${equipment.containerIdentificationNumber}`,
              identification: equipment.containerIdentificationNumber,
              model: equipment,
              type: "equipment"
            });
          }
        }
      } else if (xmlPath.includes('HouseConsignment/TransportEquipment') && !isConsignmentItemInHouse) {
        // TransportEquipment directly under HouseConsignment (not in ConsignmentItem)
        const equipment = this.resolveTransportEquipment(consignment, indices['TransportEquipment']);
        if (equipment) {
          pointers.push({
            description: `Equipment ${equipment.containerIdentificationNumber}`,
            identification: equipment.containerIdentificationNumber,
            model: equipment,
            type: "equipment"
          });
        }
      }
    } else {
      // Fallback: house consignment not found at that index, point to master
      pointers.push({
        description: `Master B/L ${consignment.consignmentMasterLevel.consignmentNumber}`,
        identification: consignment.consignmentMasterLevel.consignmentNumber,
        model: consignment.consignmentMasterLevel,
        type: "masterLevel"
      });
    }

    // Add the field-level pointer with screenPointer for highlighting
    this.addFieldPointer(pointers, xmlPath, label, consignment, houseConsignment, indices);
    return pointers;
  }

  /**
   * Adds a field-level pointer (the specific field to highlight) to the pointer chain.
   * For party-related paths (Consignor, Consignee, NotifyParty), an intermediate "Parties"
   * pointer is added first so the Parties tab gets highlighted (matching ENS behavior).
   * The model on party field pointers is set to the actual party object so the
   * consignment-party-details component can filter errors with `p.model === this.party`.
   */
  private static addFieldPointer(
    pointers: ErrorPointer[],
    xmlPath: string,
    label: string,
    consignment?: IE3ConsignmentProcess,
    houseConsignment?: IE3ConsignmentHouseLevel,
    indices?: Record<string, number>
  ): void {
    const parentModel = pointers[pointers.length - 1]?.model;
    const parentType = pointers[pointers.length - 1]?.type;

    // Add intermediate "Parties" tab pointer for party-related fields
    if (this.isPartyPath(xmlPath)) {
      pointers.push({
        description: "Parties",
        screenPointer: "Parties",
        model: parentModel,
        type: parentType
      });

      // Resolve the actual party model object for field-level highlighting
      const party = this.resolvePartyModel(xmlPath, consignment, houseConsignment);
      const partyType = this.getPartyType(xmlPath);
      const partyLabel = this.getPartyLabel(xmlPath);

      // Add the party-level pointer (e.g., "Consignee", "Consignor") for breadcrumb context
      if (party) {
        pointers.push({
          description: partyLabel,
          screenPointer: partyLabel,
          model: party,
          type: partyType
        });
      }

      // Add the field-level pointer (e.g., "House number", "City")
      const screenPointer = this.getScreenPointer(xmlPath, null, indices);
      if (screenPointer && party) {
        pointers.push({
          description: label,
          screenPointer: screenPointer,
          model: party,
          type: partyType
        });
      } else if (screenPointer) {
        pointers.push({
          description: label,
          screenPointer: screenPointer,
          model: parentModel,
          type: parentType
        });
      }
      return;
    }

    // For non-party fields, point to the specific field
    const screenPointer = this.getScreenPointer(xmlPath, null, indices);
    if (screenPointer) {
      pointers.push({
        description: label,
        screenPointer: screenPointer,
        model: parentModel,
        type: parentType
      });
    }
  }

  /**
   * Resolves the actual party object (IE3Party) from the consignment model
   * so that the consignment-party-details component can filter on `p.model === this.party`.
   */
  private static resolvePartyModel(
    xmlPath: string,
    consignment?: IE3ConsignmentProcess,
    houseConsignment?: IE3ConsignmentHouseLevel
  ): IE3Party | null {
    if (!consignment) return null;

    const isHouseLevel = xmlPath.includes('HouseConsignment');
    const masterLevel = consignment.consignmentMasterLevel;

    if (xmlPath.includes('/Consignor/') || xmlPath.endsWith('/Consignor')) {
      return isHouseLevel
        ? houseConsignment?.consignor as unknown as IE3Party
        : masterLevel?.consignor as unknown as IE3Party;
    }
    if (xmlPath.includes('/Consignee/') || xmlPath.endsWith('/Consignee')) {
      return isHouseLevel
        ? houseConsignment?.consignee as unknown as IE3Party
        : masterLevel?.consignee as unknown as IE3Party;
    }
    if (xmlPath.includes('/NotifyParty/') || xmlPath.endsWith('/NotifyParty')) {
      // NotifyParty at master or house level - take the first one
      return isHouseLevel
        ? houseConsignment?.notifyParties?.[0] as unknown as IE3Party
        : masterLevel?.notifyParties?.[0] as unknown as IE3Party;
    }
    return null;
  }

  /**
   * Returns the type identifier for the party based on the XML path.
   */
  private static getPartyType(xmlPath: string): string {
    if (xmlPath.includes('/Consignor')) return 'consignor';
    if (xmlPath.includes('/Consignee')) return 'consignee';
    if (xmlPath.includes('/NotifyParty')) return 'notifyParty';
    if (xmlPath.includes('/Agent')) return 'agent';
    if (xmlPath.includes('/AEOMutualRecognitionParty')) return 'aeoMutualRecognitionParty';
    return 'party';
  }

  /**
   * Returns the human-readable label for the party (used in breadcrumb).
   */
  private static getPartyLabel(xmlPath: string): string {
    if (xmlPath.includes('/Consignor')) return 'Consignor';
    if (xmlPath.includes('/Consignee')) return 'Consignee';
    if (xmlPath.includes('/NotifyParty')) return 'Notify party';
    if (xmlPath.includes('/Agent')) return 'Agent';
    if (xmlPath.includes('/AEOMutualRecognitionParty')) return 'Supply chain actor';
    return 'Party';
  }

  /**
   * Resolves Communication index to either 'Telephone' or 'Email address'.
   * In the TSD XML, Communication[1] = Telephone, Communication[2] = Email address.
   */
  private static resolveCommunicationType(indices?: Record<string, number>): string {
    const commIndex = indices?.['Communication'];
    if (commIndex === 1) return 'Telephone';
    if (commIndex === 2) return 'Email address';
    return 'Telephone';
  }

  /**
   * Resolves the sub-path within a party (Consignee/Consignor/NotifyParty) to a screenPointer label.
   * All three parties share the same field structure.
   */
  private static resolvePartyFieldPointer(subPath: string | undefined, indices?: Record<string, number>): string {
    if (!subPath) return 'Name';
    switch (subPath) {
      case 'Name': return 'Name';
      case 'ID': return 'EORI number';
      case 'PersonCategoryCode': return 'Type of person';
      case 'Address/CityName': return 'City';
      case 'Address/CountryCode': return 'Country';
      case 'Address/PostcodeID': return 'Postal code';
      case 'Address/StreetName': return 'Street';
      case 'Address/StreetNumberID': return 'Number';
      case 'Address/Line': return 'Street';
      case 'Address/PostOfficeBoxID': return 'Post office box';
      case 'Address/CountrySubDivisionName': return 'Region';
      case 'Communication/ID': return this.resolveCommunicationType(indices);
      case 'Communication/TypeCode': return this.resolveCommunicationType(indices);
      default: return subPath;
    }
  }

  /**
   * Determines if a path is party-related (Consignor, Consignee, NotifyParty, Agent, AEOMutualRecognitionParty)
   * and should trigger the "Parties" tab highlighting.
   */
  private static isPartyPath(xmlPath: string): boolean {
    return /\/(Consignor|Consignee|NotifyParty|Agent|AEOMutualRecognitionParty)(\/|$)/.test(xmlPath);
  }

  /**
   * Determines the screenPointer value (used for field highlighting in the UI)
   * based on the XML path and its definition in the mapping.
   */
  private static getScreenPointer(xmlPath: string, _pathDef?: any, indices?: Record<string, number>): string | null {
    if (!xmlPath) return null;

    const lastSegment = xmlPath.split('/').pop();

    // Party fields (Consignee, Consignor, NotifyParty) - share the same field structure
    const partyFieldMatch = xmlPath.match(/\/(Consignee|Consignor|NotifyParty)(\/(.+))?$/);
    if (partyFieldMatch) {
      const subPath = partyFieldMatch[3]; // everything after the party name
      return this.resolvePartyFieldPointer(subPath, indices);
    }

    // Agent fields
    if (xmlPath.endsWith('/Agent') || xmlPath.endsWith('/Agent/Name')) return 'Agent';
    if (xmlPath.endsWith('/Agent/ID')) return 'EORI number';
    if (xmlPath.endsWith('/Agent/Communication/ID')) return 'Communication number';

    // AEOMutualRecognitionParty fields
    if (xmlPath.endsWith('/AEOMutualRecognitionParty/ID')) return 'EORI number';
    if (xmlPath.endsWith('/AEOMutualRecognitionParty/RoleCode')) return 'Type';

    // Commodity / Goods fields
    if (xmlPath.endsWith('/Commodity/Description')) return 'Description of goods';
    if (xmlPath.endsWith('/Commodity/Classification/ID')) return 'HS or CN code';
    if (xmlPath.endsWith('/GoodsMeasure/GrossMassMeasure')) return 'Gross weight (kg)';

    // Packaging fields
    if (xmlPath.endsWith('/Packaging/MarksNumbersID')) return 'Shipping marks';
    if (xmlPath.endsWith('/Packaging/QuantityQuantity')) return 'Amount';
    if (xmlPath.endsWith('/Packaging/TypeCode')) return 'Package type';

    // Transport equipment
    if (lastSegment === 'TransportEquipment' || xmlPath.endsWith('/TransportEquipment/ID')) return 'Equipment number';
    if (xmlPath.endsWith('/TransportEquipment/FullnessCode')) return 'Loaded status';
    if (xmlPath.endsWith('/TransportEquipment/Seal/ID')) return 'Seals';

    // TransportContractDocument
    if (xmlPath.endsWith('/TransportContractDocument/ID')) return 'Transport document number';
    if (xmlPath.endsWith('/TransportContractDocument/TypeCode')) return 'Transport document type';

    // PreviousDocument
    if (xmlPath.endsWith('/PreviousDocument') || xmlPath.endsWith('/PreviousDocument/ID')) return 'Previous document number';
    if (xmlPath.endsWith('/PreviousDocument/TypeCode')) return 'Previous document type';
    if (xmlPath.endsWith('/PreviousDocument/LineNumeric')) return 'Previous document line number';

    // SupportingDocument
    if (xmlPath.endsWith('/SupportingDocument/ID')) return 'Supporting document number';
    if (xmlPath.endsWith('/SupportingDocument/TypeCode')) return 'Supporting document type';

    // Location fields
    if (xmlPath.endsWith('/LoadingLocation/Name') || xmlPath.endsWith('/LoadingLocation/ID')) return 'Place of loading';
    if (xmlPath.endsWith('/LoadingLocation/Address/CountryCode')) return 'Place of loading';
    if (xmlPath.endsWith('/UnloadingLocation/ID')) return 'Port of discharge';
    if (xmlPath.endsWith('/GoodsLocation/Address/PostcodeID')) return 'Goods location postcode';
    if (xmlPath.endsWith('/GoodsLocation/Address/StreetNumberID')) return 'Goods location house number';
    if (xmlPath.endsWith('/GoodsLocation/Address/CountryCode')) return 'Goods location country';

    // Warehouse
    if (xmlPath.endsWith('/Warehouse/ID')) return 'Terminal License';
    if (xmlPath.endsWith('/Warehouse/TypeCode')) return 'Terminal License';

    // Arrival transport means - maps to Vessel field in UI
    if (xmlPath.endsWith('/ArrivalTransportMeans/ID')) return 'Vessel';
    if (xmlPath.endsWith('/ArrivalTransportMeans/IdentificationTypeCode')) return 'Vessel';

    // Consignment level
    if (xmlPath.endsWith('/ExpectedArrivalDateTime')) return 'Estimated time of arrival';
    if (xmlPath.endsWith('/TotalGrossMassMeasure')) return 'Total gross weight';
    if (xmlPath.endsWith('/UCR/TraderAssignedReferenceID')) return 'Trader assigned reference';

    // Declaration level
    if (xmlPath === 'Declaration/ID') return 'MRN';
    if (xmlPath === 'Declaration/TypeCode') return 'Declaration type';
    if (xmlPath === 'Declaration/FunctionalReferenceID') return 'Functional reference number';

    // SplitConsignment
    if (xmlPath.endsWith('/SplitConsignment/IndicationIndicator')) return 'Split consignment indicator';
    if (xmlPath.endsWith('/SplitConsignment/PreviousMovementReferenceNumberID')) return 'Previous MRN';

    // SupervisingOffice
    if (xmlPath.endsWith('/SupervisingOffice/ID')) return 'Supervising office';

    // Fallback: use last segment name
    return lastSegment || null;
  }

  /**
   * Resolves a goods item based on the 1-based index from the XML path.
   * ConsignmentItem has has_sequence_attribute=true, meaning the index corresponds to goodsItemNumber.
   */
  private static resolveGoodsItem(goodsItems: IE3GoodsItem[], xmlIndex: number | undefined): IE3GoodsItem | null {
    if (!goodsItems?.length || xmlIndex == null) {
      return goodsItems?.[0] || null;
    }
    // The index from the tag is 1-based positional within the parent
    const positionalIndex = xmlIndex - 1;
    return goodsItems[positionalIndex] || null;
  }

  /**
   * Resolves transport equipment from the consignment's transportEquipmentMap by 1-based positional index.
   */
  private static resolveTransportEquipment(consignment: IE3ConsignmentProcess, xmlIndex: number | undefined): IE3TransportEquipment | null {
    const equipmentEntries = Object.values(consignment.consignmentMasterLevel.transportEquipmentMap || {});
    if (!equipmentEntries.length) {
      return null;
    }
    if (xmlIndex == null) {
      return equipmentEntries[0] || null;
    }
    const positionalIndex = xmlIndex - 1;
    return equipmentEntries[positionalIndex] || null;
  }
}
